import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Skeleton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDateTimePicker } from "@mui/x-date-pickers";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import dayjs from "dayjs";
import SendLocation from "./SendLocation";
import IndividualsAffectedField from "./IndividualsAffected";
import BodyPartSelector from "./BodyPartSelector";
import EmergencyCall from "./EmergencyCall";
import Equipment from "./Equipment";
import Material from "./Material";
import { shouldDisableDate } from "../../services/helper";
import { getFieldStyle, scrollToField } from "../fieldHelpers";

const AccidentForm = ({
  formData,
  handleChange,
  individualsList,
  setIndividualsList,
  involvesPerson,
  setInvolvesPerson,
  involvesEquipment,
  setInvolvesEquipment,
  involvesMaterial,
  setInvolvesMaterial,
  fileNames,
  setFileNames,
  highlightedField,
}) => {
  const [showLocationField, setShowLocationField] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [bodyPartsData, setBodyPartsData] = useState([]);
  const [hazards, setHazards] = useState([]);
  // const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const idRef = useRef(null);
  const categoryTypeRef = useRef(null);
  const descriptionRef = useRef(null);
  const enterLocationRef = useRef(null);
  const sendLocationRef = useRef(null);
  const reportedByRef = useRef(null);
  const otherReporterRef = useRef(null);
  const dateOfObservationRef = useRef(null);
  const attachedFileRef = useRef(null);
  const individualsAffectedRef = useRef(null);
  const equipmentListRef = useRef(null);
  const materialListRef = useRef(null);
  const severityRef = useRef(null);
  const riskRef = useRef(null);
  const priorityRef = useRef(null);
  const additionalCommentsRef = useRef(null);

  // Group refs into a memoized object
  const fieldRefs = useMemo(
    () => ({
      id: idRef,
      categoryType: categoryTypeRef,
      description: descriptionRef,
      enterLocation: enterLocationRef,
      sendLocation: sendLocationRef,
      reportedBy: reportedByRef,
      otherReporter: otherReporterRef,
      dateOfObservation: dateOfObservationRef,
      attachedFile: attachedFileRef,
      individualsAffected: individualsAffectedRef,
      equipmentList: equipmentListRef,
      materialList: materialListRef,
      severity: severityRef,
      risk: riskRef,
      priority: priorityRef,
      additionalComments: additionalCommentsRef,
    }),
    [],
  );

  useEffect(() => {
    scrollToField(highlightedField, fieldRefs); // Use the external helper
  }, [highlightedField, fieldRefs]);

  useEffect(() => {
    const fetchBaseData = async () => {
      setLoading(true);
      const hazardsData = JSON.parse(localStorage.getItem("hazards")) || [];
      // const employeeData = JSON.parse(localStorage.getItem("reportedBy")) || [];
      setHazards(hazardsData ?? []);
      // setEmployees(employeeData ?? []);
      setLoading(false);
    };
    fetchBaseData();
  }, []);

  useEffect(() => {
    if (
      Array.isArray(formData.individuals) &&
      formData.individuals.length > 0
    ) {
      setInvolvesPerson(true);
    }
    if (
      Array.isArray(formData.equipmentList) &&
      formData.equipmentList.length > 0
    ) {
      setInvolvesEquipment(true);
    }
    if (
      Array.isArray(formData.materialList) &&
      formData.materialList.length > 0
    ) {
      setInvolvesMaterial(true);
    }

    if (
      Array.isArray(formData.bodyPartsData) &&
      formData.bodyPartsData.length > 0
    ) {
      setBodyPartsData(formData.bodyPartsData);
    }
  }, [
    formData.equipmentList,
    formData.individuals,
    formData.materialList,
    formData.bodyPartsData,
    setInvolvesEquipment,
    setInvolvesMaterial,
    setInvolvesPerson,
  ]);

  // Function to handle selecting an individual from the list
  const handleSelectIndividual = (individual, index) => {
    setSelectedIndividual(index);

    // Check if the individual's data already exists in bodyPartsData; if not, initialize it
    if (!bodyPartsData.some((data) => data.id === index)) {
      setBodyPartsData((prevData) => [
        ...prevData,
        { id: index, name: individual, bodyParts: {}, conscious: false },
      ]);
    }
  };
  const toggleLocationField = () => {
    setShowLocationField((prev) => !prev);
  };

  const uploadFile = (e) => {
    const file = e.target?.files[0];
    if (file) {
      let storedFile = new File([file], file.name, { type: file.type });
      setFileNames((prev) => [...prev, storedFile]);
      handleChange(e);
    }
  };

  const previewFile = (file) => {
    if (!file.url) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileContent = event.target.result;
        const blob = new Blob([fileContent], { type: file.type });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      };

      reader.onerror = (error) => {
        console.error("Error reading file:", error);
      };

      reader.readAsArrayBuffer(file);
    } else {
      window.open(file.url, "_blank");
    }
  };

  const removeFile = (fileRemove) => {
    setFileNames((prev) =>
      prev.filter((file) => file.name !== fileRemove.name),
    );
    handleChange({ target: { name: "removeFile", value: fileRemove } });
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormControl
          ref={fieldRefs["dateOfObservation"]}
          fullWidth
          margin="normal"
          required
        >
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Time & Date of Observation
          </Typography>
          <MobileDateTimePicker
            fullWidth
            name="dateOfObservation"
            sx={getFieldStyle("dateOfObservation", highlightedField)}
            shouldDisableDate={shouldDisableDate}
            value={
              formData.dateOfObservation
                ? dayjs(formData.dateOfObservation.$d)
                : null
            }
            onChange={(newValue) =>
              handleChange({
                target: { name: "dateOfObservation", value: newValue },
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ marginTop: "2.5px" }}
                variant="outlined"
              />
            )}
            format="DD/MM/YY hh:mm A" // Includes AM/PM
          />
        </FormControl>
      </LocalizationProvider>
      <FormControl
        fullWidth
        margin="normal"
        ref={fieldRefs["categoryType"]}
        required
      >
        <Typography
          id="incident-type-label"
          variant="body2"
          sx={{ color: "#888", marginTop: "10px" }}
        >
          Select Accident Type
        </Typography>
        <>
          {!loading ? (
            <Select
              labelId="incident-type-label"
              name="categoryType"
              sx={getFieldStyle("categoryType", highlightedField)}
              value={formData.categoryType || ""}
              onChange={handleChange}
            >
              {hazards.map((hazard) => (
                <MenuItem value={hazard.id} key={hazard.id}>
                  {hazard.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Skeleton variant="rectangular" width={350} height={30} />
          )}
        </>
      </FormControl>
      {formData.categoryType === "other" && (
        <TextField
          fullWidth
          margin="normal"
          name="otherType"
          ref={fieldRefs["otherType"]}
          sx={getFieldStyle("otherType", highlightedField)}
          label="Specify Accident Type"
          value={formData.otherType || ""}
          onChange={handleChange}
        />
      )}
      <TextField
        fullWidth
        margin="normal"
        name="description"
        ref={fieldRefs["description"]}
        sx={getFieldStyle("description", highlightedField)}
        label="Add Description"
        value={formData.description || ""}
        onChange={handleChange}
        required
      />
      <Box>
        {!loading ? (
          <TextField
            fullWidth
            margin="normal"
            name="reportedBy"
            ref={fieldRefs["reportedBy"]}
            sx={getFieldStyle("reportedBy", highlightedField)}
            label="Reported By"
            value={formData.reportedBy || ""}
            onChange={handleChange}
            required
          >
            {/* {employees.map((employee) => (
              <MenuItem value={employee.id} key={employee.id}>
                {employee.name}
              </MenuItem>
            ))} */}
          </TextField>
        ) : (
          <Skeleton variant="rectangular" width={350} height={30} />
        )}
      </Box>
      {formData.reportedBy === "other" && (
        <TextField
          fullWidth
          margin="normal"
          name="otherReporter"
          ref={fieldRefs["otherReporter"]}
          sx={getFieldStyle("otherReporter", highlightedField)}
          label="Other Reporter"
          value={formData.otherReporter || ""}
          onChange={handleChange}
        />
      )}
      <SendLocation
        formData={formData}
        handleChange={handleChange}
        ref={fieldRefs["sendLocation"]}
        sx={getFieldStyle("sendLocation", highlightedField)}
        highlighted={highlightedField === "sendLocation"}
      />
      {showLocationField && (
        <>
          <Typography align="center">OR</Typography>
          <TextField
            fullWidth
            margin="normal"
            name="enterLocation"
            ref={fieldRefs["enterLocation"]}
            sx={getFieldStyle("enterLocation", highlightedField)}
            label="Enter Location Manually"
            value={formData.enterLocation || ""}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleLocationField}>
                    <LocationOnIcon /> {/* Same icon to close the field */}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}
      {/* <OtherDamages formData={formData} handleChange={handleChange} /> */}
      <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
        The accident Involves:
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={involvesPerson}
              onChange={() => setInvolvesPerson(!involvesPerson)}
            />
          }
          label="Person"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={involvesEquipment}
              onChange={() => setInvolvesEquipment(!involvesEquipment)}
            />
          }
          label="Equipment"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={involvesMaterial}
              onChange={() => setInvolvesMaterial(!involvesMaterial)}
            />
          }
          label="Material"
        />
      </FormGroup>
      {involvesPerson && (
        <>
          <IndividualsAffectedField
            formData={formData}
            handleChange={handleChange}
            individualsList={individualsList}
            setIndividualsList={setIndividualsList}
            setSelectedIndividual={handleSelectIndividual}
            ref={fieldRefs["individualsAffected"]}
            sx={getFieldStyle("individualsAffected", highlightedField)}
            highlighted={highlightedField === "individualsAffected"}
            type={"Accident"}
            bodyPartsData={bodyPartsData}
            setBodyPartsData={setBodyPartsData}
          />
          {selectedIndividual !== null && (
            <BodyPartSelector
              formData={formData}
              selectedIndividual={selectedIndividual}
              bodyPartsData={bodyPartsData}
              setBodyPartsData={setBodyPartsData}
            />
          )}
        </>
      )}
      {involvesEquipment && (
        <Equipment
          formData={formData}
          handleChange={handleChange}
          ref={fieldRefs["equipmentList"]}
          highlighted={highlightedField === "equipmentList"}
        />
      )}
      {involvesMaterial && (
        <Material
          formData={formData}
          handleChange={handleChange}
          ref={fieldRefs["materialList"]}
          highlighted={highlightedField === "materialList"}
        />
      )}
      <Button
        variant="outlined"
        component="label"
        ref={fieldRefs["attachedFile"]}
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: "16px",
          paddingTop: "16px",
          paddingBottom: "16px",
          marginBottom: "8px",
          ...getFieldStyle("attachedFile", highlightedField),
        }}
      >
        <AttachFileIcon sx={{ marginRight: "8px" }} />
        Add File
        <input
          type="file"
          hidden
          name="attachedFile"
          onChange={(e) => {
            uploadFile(e);
          }}
        />
      </Button>
      {/* Display Uploaded File Names with Remove Option */}
      <Box sx={{ marginTop: "10px" }}>
        {fileNames.map((file, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              marginTop: "5px",
            }}
          >
            <Typography variant="body2">{file.name}</Typography>
            <Box>
              <Button
                color="primary"
                size="small"
                sx={{ marginRight: "8px" }}
                onClick={() => previewFile(file)}
              >
                Preview
              </Button>
              <Button
                color="error"
                size="small"
                onClick={() => removeFile(file)}
              >
                Remove
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <FormControl
        fullWidth
        margin="normal"
        ref={fieldRefs["additionalComments"]}
        required
      >
        <Typography
          id="next-step-label"
          variant="body2"
          sx={{ color: "#888", marginTop: "10px" }}
        >
          Additional comments
        </Typography>
        <TextField
          id="additional-comments"
          name="additionalComments"
          value={formData.additionalComments || ""}
          onChange={handleChange}
          multiline
          rows={4} // You can adjust the number of rows to control the height of the textarea
          variant="outlined"
          placeholder="Enter your comments here..."
          sx={{
            marginTop: 1,
            ...getFieldStyle("additionalComments", highlightedField),
          }}
        />
      </FormControl>
      <FormControl
        component="fieldset"
        margin="normal"
        sx={{ marginTop: "20px" }}
      >
        <Typography variant="body2" sx={{ color: "#888" }}>
          Reportable Accident?
        </Typography>
        <RadioGroup
          row
          name="reportableAccident"
          value={formData.reportableAccident || ""}
          onChange={handleChange}
        >
          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="No" control={<Radio />} label="No" />
        </RadioGroup>
        {formData.reportableAccident === "Yes" && (
          <>
            <Typography
              id="action-required-label"
              variant="body2"
              sx={{ color: "#888", marginTop: "30px", marginBottom: "-15px" }}
            >
              Action Required
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
              }}
            >
              <EmergencyCall />
              <Button
                variant="contained"
                sx={{ ml: "2.5px", fontSize: "12px", lineHeight: "18px" }}
                color="primary"
              >
                First Aid
              </Button>
            </Box>
          </>
        )}
      </FormControl>
    </>
  );
};
export default AccidentForm;
